import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Smartphone, Share, PlusSquare, MoreVertical, X } from "lucide-react";

// The browser offers its install prompt once per page load — keep it for whoever taps "Get the app".
let deferred = null;
if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => { e.preventDefault(); deferred = e; });
}

const standalone = () =>
  typeof window !== "undefined" && (window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone);

const platform = () => {
  const ua = navigator.userAgent;
  if (/iphone|ipad|ipod/i.test(ua)) return /crios|fxios|edgios/i.test(ua) ? "ios-other" : "ios";
  return /android/i.test(ua) ? "android" : "desktop";
};

const STEPS = {
  ios: [<>Tap <Share className="inline size-4 text-[#0055FF]" /> Share at the bottom of Safari</>, <>Choose <PlusSquare className="inline size-4 text-[#0055FF]" /> Add to Home Screen</>, "Open Connexis from your home screen"],
  "ios-other": ["Open connex.is in Safari (Apple only lets Safari add apps)", <>Tap <Share className="inline size-4 text-[#0055FF]" /> Share, then Add to Home Screen</>, "Open Connexis from your home screen"],
  android: [<>Tap <MoreVertical className="inline size-4 text-[#0055FF]" /> the menu in Chrome</>, "Choose Install app (or Add to Home screen)", "Open Connexis from your home screen"],
  desktop: ["On your phone, open connex.is and sign in", "Tap Get the app at the top of the screen", "On this computer, Chrome and Edge also show an install icon at the right of the address bar"],
};

function HowTo({ onClose }) {
  const steps = STEPS[platform()];
  return (
    <div className="fixed inset-0 z-[80] grid place-items-end sm:place-items-center bg-black/40 p-3" onClick={onClose} data-testid="get-app-help">
      <div className="w-full max-w-sm rounded-2xl bg-white p-5 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3">
          <img src="/pwa/icon-192.png" alt="" className="size-10 rounded-xl" />
          <div className="flex-1">
            <div className="text-[15px] font-semibold text-slate-900">Get the Connexis app</div>
            <div className="text-[12px] text-slate-500">Free, no app store needed, and you get alerts on your phone.</div>
          </div>
          <button onClick={onClose} className="grid size-8 place-items-center rounded-full text-slate-400 hover:bg-slate-100" aria-label="Close" data-testid="get-app-help-close">
            <X className="size-4" />
          </button>
        </div>
        <ol className="mt-4 space-y-2.5">
          {steps.map((s, i) => (
            <li key={i} className="flex items-start gap-3 text-[13.5px] text-slate-700">
              <span className="grid size-6 shrink-0 place-items-center rounded-full bg-[#0055FF]/10 text-[12px] font-semibold text-[#0055FF]">{i + 1}</span>
              <span className="pt-0.5">{s}</span>
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
}

/** Shown in every portal and the Hub until this device runs Connexis as an installed app. */
export function GetAppButton({ compact = false }) {
  const [installed, setInstalled] = useState(standalone);
  const [help, setHelp] = useState(false);
  useEffect(() => {
    const on = () => setInstalled(true);
    window.addEventListener("appinstalled", on);
    return () => window.removeEventListener("appinstalled", on);
  }, []);
  if (installed) return null;

  const tap = async () => {
    if (deferred) {
      const e = deferred;
      deferred = null;
      try { e.prompt(); if ((await e.userChoice)?.outcome === "accepted") setInstalled(true); return; } catch { /* fall back to the steps */ }
    }
    setHelp(true);
  };

  return (
    <>
      <button onClick={tap} className={`get-app${compact ? " compact" : ""}`} aria-label="Get the Connexis app"
              data-testid={compact ? "get-app-btn-mobile" : "get-app-btn"}>
        <Smartphone /> <span>{compact ? "App" : "Get the app"}</span>
      </button>
      {help && createPortal(<HowTo onClose={() => setHelp(false)} />, document.body)}
    </>
  );
}

export default GetAppButton;
